import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check } from 'lucide-react';
import { UserAvatar, useGql } from '@ah/design';
import { Header, PullToRefresh } from '@ah/design/mobile';
import {
  NOTIFICATIONS_QUERY,
  MARK_NOTIFICATION_READ,
  MARK_ALL_NOTIFICATIONS_READ,
  NOTIFICATION_ADDED_SUB,
  UNREAD_COUNT_SUB,
  navigateToUrl,
  type AppNotification,
} from '@ah/notifications/shared';
import { PushToggle } from '@ah/notifications/mobile';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export function Notifications() {
  const gql = useGql();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      gql.request<{ notifications: AppNotification[]; unreadNotificationCount: number }>(
        NOTIFICATIONS_QUERY,
        { limit: 50 },
      ),
  });

  useEffect(() => {
    const invalidate = () => qc.invalidateQueries({ queryKey: ['notifications'] });
    const unsubAdded = gql.subscribe(NOTIFICATION_ADDED_SUB, {}, invalidate);
    const unsubUnread = gql.subscribe(UNREAD_COUNT_SUB, {}, invalidate);
    return () => {
      unsubAdded();
      unsubUnread();
    };
  }, [gql, qc]);

  const items = data?.notifications ?? [];
  const unread = data?.unreadNotificationCount ?? 0;

  const handleItemClick = async (item: AppNotification) => {
    if (!item.readAt) {
      try {
        await gql.request(MARK_NOTIFICATION_READ, { id: item.id });
      } catch {
        // non-fatal
      }
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }
    if (item.targetUrl) navigateToUrl(navigate, item.targetUrl);
  };

  const handleMarkAll = async () => {
    try {
      await gql.request(MARK_ALL_NOTIFICATIONS_READ, {});
    } catch {
      // non-fatal
    }
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Notifications" backTo="/" />
      <PullToRefresh
        className="flex-1"
        onRefresh={() => qc.invalidateQueries({ queryKey: ['notifications'] })}
      >
        <PushToggle />

        {unread > 0 && (
          <div className="flex justify-end px-4 py-2 border-b border-border">
            <button
              type="button"
              onClick={handleMarkAll}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Check size={14} /> Mark all read
            </button>
          </div>
        )}

        {isLoading && <NotificationsSkeleton />}

        {!isLoading && items.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <Bell size={32} className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <ul>
            {items.map((item) => {
              const isUnread = !item.readAt;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-border active:bg-muted ${isUnread ? 'bg-primary/5' : ''}`}
                  >
                    <UserAvatar
                      src={item.sender?.pictureUrl ?? undefined}
                      name={item.sender?.name ?? '•'}
                      className="size-10 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold truncate">{item.title}</p>
                        <span className="text-[11px] text-muted-foreground shrink-0">
                          {timeAgo(item.createdAt)}
                        </span>
                      </div>
                      <p
                        className={`text-sm mt-0.5 line-clamp-2 ${isUnread ? 'text-foreground' : 'text-muted-foreground'}`}
                      >
                        {item.body}
                      </p>
                    </div>
                    {isUnread && (
                      <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-2" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </PullToRefresh>
    </div>
  );
}

function NotificationsSkeleton() {
  return (
    <ul className="animate-pulse">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="flex items-start gap-3 px-4 py-3 border-b border-border">
          <div className="size-10 rounded-full bg-muted shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/2 rounded bg-muted" />
            <div className="h-3 w-full rounded bg-muted" />
          </div>
        </li>
      ))}
    </ul>
  );
}
